/*
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { BufferAttribute } from 'three';
import { ChannelType } from '../CrownDesc';
import { CrownShapeCapUtil } from './CrownShapeCapUtil';
import { ShaderAccessor } from './ShaderAccessor';
import { MaterialCache, PointsMaterialShape } from '@sick-davinci/glow';
import { CrownPointsMaterial, CrownPoinsMaterialColorMode, CrownPointsMaterialColorAttribute } from '../CrownPointsMaterial';
const channelTypeKeyNamed = {
    /*0*/ BELOW_LOWER_SIGNAL_THRESHOLD: { bitSet: 1, bitScope: 1 },
    /*1*/ ABOVE_UPPER_SIGNAL_THRESHOLD: { bitSet: 1, bitScope: 1 },
    /*2*/ BELOW_LOWER_DISTANCE_THRESHOLD: { bitSet: 4, bitScope: 4 },
    /*3*/ ABOVE_UPPER_DISTANCE_THRESHOLD: { bitSet: 8, bitScope: 8 },
    /*4*/ SENDER_OFF: { bitSet: 16, bitScope: 16 },
    /*5*/ // PLACEHOLDER = 32,
    /*6*/ // PLACEHOLDER = 64,
    /*7*/ // PLACEHOLDER = 128,
    /*8*/ // PLACEHOLDER = 256,
    /*9*/ // PLACEHOLDER = 512,
    /*10-12: 000*/ AMBIENT_LIGHT_0: { bitSet: 0, bitScope: 7168 },
    /*10-12  001*/ AMBIENT_LIGHT_1: { bitSet: 1024, bitScope: 7168 },
    /*10-12  010*/ AMBIENT_LIGHT_2: { bitSet: 2048, bitScope: 7168 },
    /*10-12  011*/ AMBIENT_LIGHT_3: { bitSet: 3072, bitScope: 7168 },
    /*10-12  100*/ AMBIENT_LIGHT_4: { bitSet: 4096, bitScope: 7168 },
    /*10-12  101*/ AMBIENT_LIGHT_5: { bitSet: 5120, bitScope: 7168 },
    /*10-12  110*/ AMBIENT_LIGHT_6: { bitSet: 6144, bitScope: 7168 },
    /*10-12  111*/ AMBIENT_LIGHT_7: { bitSet: 7168, bitScope: 7168 },
    /*13*/ REFLECTOR: { bitSet: 8192, bitScope: 8192 },
    /*14-15  00 */ EDGE_HIT_0: { bitSet: 0, bitScope: 49152 },
    /*14-15  01 */ EDGE_HIT_1: { bitSet: 16384, bitScope: 49152 },
    /*14-15  10 */ EDGE_HIT_2: { bitSet: 32768, bitScope: 49152 },
    /*14-15  11 */ EDGE_HIT_3: { bitSet: 49152, bitScope: 49152 },
    /*16*/ // PLACEHOLDER = 65536,
    /*17*/ // PLACEHOLDER = 131072,
    /*18*/ // PLACEHOLDER = 262144,
    /*19*/ // PLACEHOLDER = 524288,
    /*20*/ // PLACEHOLDER = 1048576,
    /*21*/ // PLACEHOLDER = 2097152,
    /*22*/ // PLACEHOLDER = 4194304,
    /*23*/ // PLACEHOLDER = 8388608,
    /*24*/ // PLACEHOLDER = 16777216,
    /*25*/ // PLACEHOLDER = 33554432,
    /*26*/ // PLACEHOLDER = 67108864,
    /*27*/ // PLACEHOLDER = 134217728,
    /*28*/ LMS4000_POSSIBLE_OUTLIER: { bitSet: 268435456, bitScope: 268435456 },
    /*29*/ LMS4000_PARTIAL_GLOSS: { bitSet: 536870912, bitScope: 536870912 },
};
const channelTypeKeyBit = {
    /*0*/ BIT_0: { bitSet: 1, bitScope: 1 },
    /*1*/ BIT_1: { bitSet: 2, bitScope: 2 },
    /*2*/ BIT_2: { bitSet: 4, bitScope: 4 },
    /*3*/ BIT_3: { bitSet: 8, bitScope: 8 },
    /*4*/ BIT_4: { bitSet: 16, bitScope: 16 },
    /*5*/ BIT_5: { bitSet: 32, bitScope: 32 },
    /*6*/ BIT_6: { bitSet: 64, bitScope: 64 },
    /*7*/ BIT_7: { bitSet: 128, bitScope: 128 },
    /*8*/ BIT_8: { bitSet: 256, bitScope: 256 },
    /*9*/ BIT_9: { bitSet: 512, bitScope: 512 },
    /*10*/ BIT_10: { bitSet: 1024, bitScope: 1024 },
    /*11*/ BIT_11: { bitSet: 2048, bitScope: 2048 },
    /*12*/ BIT_12: { bitSet: 4096, bitScope: 4096 },
    /*13*/ BIT_13: { bitSet: 8192, bitScope: 8192 },
    /*14*/ BIT_14: { bitSet: 16384, bitScope: 16384 },
    /*15*/ BIT_15: { bitSet: 32768, bitScope: 32768 },
    /*16*/ BIT_16: { bitSet: 65536, bitScope: 65536 },
    /*17*/ BIT_17: { bitSet: 131072, bitScope: 131072 },
    /*18*/ BIT_18: { bitSet: 262144, bitScope: 262144 },
    /*19*/ BIT_19: { bitSet: 524288, bitScope: 524288 },
    /*20*/ BIT_20: { bitSet: 1048576, bitScope: 1048576 },
    /*21*/ BIT_21: { bitSet: 2097152, bitScope: 2097152 },
    /*22*/ BIT_22: { bitSet: 4194304, bitScope: 4194304 },
    /*23*/ BIT_23: { bitSet: 8388608, bitScope: 8388608 },
    /*24*/ BIT_24: { bitSet: 16777216, bitScope: 16777216 },
    /*25*/ BIT_25: { bitSet: 33554432, bitScope: 33554432 },
    /*26*/ BIT_26: { bitSet: 67108864, bitScope: 67108864 },
    /*27*/ BIT_27: { bitSet: 134217728, bitScope: 134217728 },
    /*28*/ BIT_28: { bitSet: 268435456, bitScope: 268435456 },
    /*29*/ BIT_29: { bitSet: 536870912, bitScope: 536870912 },
    /*30*/ BIT_30: { bitSet: 1073741824, bitScope: 1073741824 },
    /*31*/ BIT_31: { bitSet: 2147483648, bitScope: 2147483648 },
};
/**
 * Updates the shader buffers depending on the properties channel and the setColor function from ScanDecoration
 */
export class ShaderAccessorColorProperties extends ShaderAccessor {
    constructor(echoIdx) {
        super(echoIdx);
        this._materialCache = new MaterialCache();
    }
    updateShader(geometry, decoration) {
        if (this._polarScan.PropertiesValues != null) {
            // create an color array for each scanpoint (dist values array size)
            const rgb = new Float32Array(this._polarScan.DistValues[this._echoIdx].numOfElems * 3);
            geometry.addAttribute('preliminaryColor', new BufferAttribute(rgb, 3));
            this._shaderMaterial = this._materialCache.getMaterial(decoration, () => {
                return new CrownPointsMaterial({
                    usePolarDataBuffers: true,
                    interpolationMode: CrownPoinsMaterialColorMode.STATIC,
                    colorAttribute: CrownPointsMaterialColorAttribute.PRELIMARY_COLOR,
                    sizeAttenuation: false,
                    size: decoration.PointSize,
                    pointsShape: PointsMaterialShape.circle,
                });
            });
            const foundColorsForEcho = this._getColors(decoration);
            // search for the ChannelType.ALL_CHANNELS and fill the channelTypeColorMap
            foundColorsForEcho.some((colorInternal) => {
                if (colorInternal.ChannelType === ChannelType.ALL_CHANNELS || colorInternal.ChannelType.toString() === '') {
                    // write a default color for every channelType
                    for (let i = 0; i < rgb.length; i = i + 3) {
                        rgb[i + 0] = colorInternal.R / 255;
                        rgb[i + 1] = colorInternal.G / 255;
                        rgb[i + 2] = colorInternal.B / 255;
                    }
                    return true;
                }
                return false;
            });
            const colorMap = new Map();
            // now search for concrete echo/label mappings for one label there can be
            // currently LABEL is the only supported ChannelType for uni color mapping
            foundColorsForEcho.forEach((colorInternal) => {
                if (colorInternal.ChannelType === ChannelType.LABEL || colorInternal.ChannelType === ChannelType.PROPERTIES) {
                    const channelTypeKeyRaw = colorInternal.ChannelTypeKey.toUpperCase();
                    let channelTypeEnumFields = [channelTypeKeyRaw];
                    if (channelTypeKeyRaw.indexOf('|') >= 0) {
                        channelTypeEnumFields = channelTypeKeyRaw.split('|').map((bitEntry) => bitEntry.trim());
                    }
                    let channelTypeEnumFieldMask = null;
                    channelTypeEnumFields.forEach((channelTypeEnumEntry) => {
                        let foundBitDef = channelTypeKeyNamed[channelTypeEnumEntry];
                        // if the named entry was not found try to get it from bitField
                        if (foundBitDef == null) {
                            foundBitDef = channelTypeKeyBit[channelTypeEnumEntry];
                        }
                        if (foundBitDef != null) {
                            if (channelTypeEnumFieldMask == null) {
                                channelTypeEnumFieldMask = {
                                    bitSet: 0,
                                    bitScope: 0,
                                };
                            }
                            // tslint:disable-next-line:no-bitwise
                            channelTypeEnumFieldMask.bitSet = channelTypeEnumFieldMask.bitSet | foundBitDef.bitSet;
                            // tslint:disable-next-line:no-bitwise
                            channelTypeEnumFieldMask.bitScope = channelTypeEnumFieldMask.bitScope | foundBitDef.bitScope;
                        }
                    });
                    if (channelTypeEnumFieldMask != null) {
                        colorMap.set(channelTypeEnumFieldMask, [colorInternal.R / 255, colorInternal.G / 255, colorInternal.B / 255, 1]);
                    }
                }
            });
            if (this._polarScan.PropertiesValues.length > 0) {
                const propertiesData = this._polarScan.PropertiesValues[this._echoIdx];
                const propertiesView = CrownShapeCapUtil.getArrayInCorrectByteOrder32(propertiesData.data, propertiesData.endian);
                const properties = new Uint32Array(propertiesView.buffer, propertiesView.byteOffset, propertiesView.byteLength / 4);
                colorMap.forEach((color, key) => {
                    for (let i = 0; i < properties.length; i++) {
                        const property = properties[i];
                        // if ((key.bitSet & property) === key.bitSet) {
                        // tslint:disable-next-line:no-bitwise
                        if (((key.bitScope & property) ^ key.bitSet) === 0) {
                            const rgbaIndex = i * 3;
                            rgb[rgbaIndex + 0] = color[0];
                            rgb[rgbaIndex + 1] = color[1];
                            rgb[rgbaIndex + 2] = color[2];
                        }
                    }
                });
            }
        }
    }
    _getColors(decoration) {
        const _colors = [];
        const colorSettingsForAllEchos = [];
        decoration.Colors.forEach((element) => {
            if (element.Echo === 0) {
                colorSettingsForAllEchos.push(element);
            }
            if ((element.Echo - 1) !== this._echoIdx) {
                return;
            }
            if ((element.ChannelType === 'LABEL' || element.ChannelType === 'PROPERTIES') && element.ChannelTypeKey !== '') {
                element.ChannelType = ChannelType.LABEL;
            }
            else {
                element.ChannelType = ChannelType.ALL_CHANNELS;
            }
            _colors.push(element);
        });
        return _colors.concat(colorSettingsForAllEchos);
    }
}
//# sourceMappingURL=ShaderAccessorColorProperties.js.map